<!-- Drawer backdrop (below 1024px) -->
@if (sidenavService.isOpen()) {
  <div
    class="fixed inset-0 z-20 bg-black/40 lg:hidden"
    (click)="sidenavService.close()"
    aria-hidden="true">
  </div>
}

<!--
  One sidebar, three modes (F17): an overlay drawer below lg, a 72px icon rail from lg to xl (labels kept for screen
  readers), and the full 248px sidebar from xl.
-->
<aside
  [class]="sidebarClasses()"
  [cdkTrapFocus]="sidenavService.isOpen()"
  aria-label="Main navigation">

  <!-- Drawer header row with close button -->
  <div class="flex items-center justify-between px-4 py-4 lg:hidden" style="box-shadow: var(--shadow-divider);">
    <span class="text-caption font-medium text-primary">Navigation</span>
    <button
      #closeButton
      type="button"
      (click)="sidenavService.close()"
      class="rounded-pill p-2 min-h-11 min-w-11 flex items-center justify-center text-secondary hover:text-primary hover:bg-grey-100 transition-nike focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      aria-label="Close navigation">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>

  <!-- Company selector (in the header on the rail) -->
  @if (mode() !== 'rail') {
    <div class="px-3 pt-3 pb-1">
      <app-company-dropdown />
    </div>
  }

  <!-- Nav links -->
  <nav class="flex flex-col gap-0.5 p-3 lg:max-xl:px-2" aria-label="Main">
    @for (item of visibleNavItems(); track trackItem(item)) {
      @if (item.kind === 'link') {
        <a
          [routerLink]="item.path"
          routerLinkActive="bg-grey-100 text-primary"
          [routerLinkActiveOptions]="{ exact: false }"
          [attr.title]="mode() === 'rail' ? item.label : null"
          class="flex items-center gap-3 rounded-md px-3 py-2.5 min-h-11 text-caption font-medium text-secondary hover:bg-grey-100 hover:text-primary transition-nike focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus lg:max-xl:justify-center lg:max-xl:px-0"
          (click)="onNavigate()">
          <svg class="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" [attr.d]="item.icon" />
          </svg>
          <span class="lg:max-xl:sr-only">{{ item.label }}</span>
        </a>
      } @else {
        <div class="relative" [attr.data-group]="item.id">
          <button
            type="button"
            [id]="'nav-group-' + item.id"
            (click)="toggleGroup(item)"
            [attr.aria-expanded]="mode() === 'rail' ? flyoutGroupId() === item.id : isExpanded(item)"
            [attr.aria-controls]="'nav-group-' + item.id + '-links'"
            [attr.title]="mode() === 'rail' ? item.label : null"
            class="w-full flex items-center gap-3 rounded-md px-3 py-2.5 min-h-11 text-caption font-medium hover:bg-grey-100 hover:text-primary transition-nike focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus lg:max-xl:justify-center lg:max-xl:px-0"
            [class.text-primary]="isGroupActive(item)"
            [class.text-secondary]="!isGroupActive(item)"
            [class.bg-grey-100]="mode() === 'rail' && isGroupActive(item)">
            <svg class="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" [attr.d]="item.icon" />
            </svg>
            <span class="flex-1 text-left lg:max-xl:sr-only">{{ item.label }}</span>
            <svg
              class="h-4 w-4 shrink-0 text-secondary transition-transform duration-200 lg:max-xl:hidden"
              [class.rotate-180]="isExpanded(item)"
              fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          @if (mode() === 'rail') {
            <!-- Rail: the sub-menu opens beside the rail -->
            @if (flyoutGroupId() === item.id) {
              <div
                [id]="'nav-group-' + item.id + '-links'"
                class="absolute left-full top-0 z-40 ml-2 flex w-56 flex-col gap-0.5 rounded-lg border border-border-subtle bg-background p-1 shadow-overlay">
                <p class="px-3 pt-2 pb-1 text-small font-semibold uppercase tracking-wide text-secondary" aria-hidden="true">{{ item.label }}</p>
                @for (child of item.children; track child.path) {
                  <a
                    [routerLink]="child.path"
                    [class.bg-grey-100]="isChildActive(child)"
                    [class.text-primary]="isChildActive(child)"
                    [class.text-secondary]="!isChildActive(child)"
                    [attr.aria-current]="isChildActive(child) ? 'page' : null"
                    class="flex items-center rounded-md px-3 py-2.5 min-h-11 text-caption font-medium hover:bg-grey-100 hover:text-primary transition-nike focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                    (click)="onNavigate()">
                    {{ child.label }}
                  </a>
                }
              </div>
            }
          } @else if (isExpanded(item)) {
            <div [id]="'nav-group-' + item.id + '-links'" class="flex flex-col gap-0.5">
              @for (child of item.children; track child.path) {
                <a
                  [routerLink]="child.path"
                  [class.bg-grey-100]="isChildActive(child)"
                  [class.text-primary]="isChildActive(child)"
                  [class.text-secondary]="!isChildActive(child)"
                  [attr.aria-current]="isChildActive(child) ? 'page' : null"
                  class="flex items-center rounded-md pl-11 pr-3 py-2.5 min-h-11 text-caption font-medium hover:bg-grey-100 hover:text-primary transition-nike focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                  (click)="onNavigate()">
                  {{ child.label }}
                </a>
              }
            </div>
          }
        </div>
      }
    }
  </nav>
</aside>
